import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Link as MaterialLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { filePsmAudit } from '../../../actionTypes';
import formatDate from '../../../utils/textFormaters/date';
import { downloadStoredPsmAuditPdf, isFollowUpAudit } from '../../../utils/psmAuditPdf';
import { mergeAndSortPsmAudits } from '../../../utils/psmAuditList';
import { useUserPermissions } from '../../../utils/effects/userPermissions';
import { userTypes } from '../../../utils/constants';
import { canEditPsmAsUser } from '../../../utils/psmAuditPermissions';
import ModalConfirmation from '../../components/ModalConfirmation';
import useStyles from './styles';

const AUDIT_TYPE_LABELS = {
  AuditReport: 'Audit Report',
  AuditFollowUp: 'Audit Follow-Up',
};

const STATUS_LABELS = {
  InProgress: 'In Progress',
  Completed: 'Completed',
  PendingCorrection: 'Pending Correction',
  SentToContractor: 'Sent to Contractor',
};

const getRowLabel = row => {
  if (row?.name) {
    return row.name;
  }
  if (row?.auditType && AUDIT_TYPE_LABELS[row.auditType]) {
    return AUDIT_TYPE_LABELS[row.auditType];
  }
  return isFollowUpAudit(row) ? 'Audit Follow-Up' : 'Audit Report';
};

const StatusText = ({ status, classes }) => {
  const statusClass = {
    InProgress: classes.statusInProgress,
    Completed: classes.statusCompleted,
    PendingCorrection: classes.statusPendingCorrection,
    SentToContractor: classes.statusSentToContractor,
  }[status];

  return (
    <Typography className={`${classes.statusText} ${statusClass || ''}`}>{STATUS_LABELS[status] || status}</Typography>
  );
};

const PsmAuditDigitalList = ({
  audits,
  uploadedAudits,
  companyId,
  deleteList,
  hasPermissions,
  onDelete,
  onDeleteUploaded,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const currentUserId = useSelector(state => state.auth.check.userId);
  const uploadedDownloadList = useSelector(state => state.filePsmAudit.downloadList);
  const { type: userType } = useUserPermissions();
  const isOwner = userType === userTypes.Owner;
  const [notCompletedOpen, setNotCompletedOpen] = useState(false);
  const [downloadErrorOpen, setDownloadErrorOpen] = useState(false);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [expandedAuditIds, setExpandedAuditIds] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const companyPath = match.url.replace(/\/$/, '');

  const topLevelAudits = mergeAndSortPsmAudits(uploadedAudits, audits);

  const toggleExpanded = auditId => {
    setExpandedAuditIds(prev => ({
      ...prev,
      [auditId]: !prev[auditId],
    }));
  };

  const handleUploadedNameClick = row => {
    dispatch({
      type: filePsmAudit.download.start,
      payload: { companyId, id: row.id },
    });
  };

  const handleDigitalNameClick = async row => {
    if (row.status !== 'Completed') {
      setNotCompletedOpen(true);
      return;
    }
    setDownloadingId(row.id);
    try {
      await downloadStoredPsmAuditPdf(row);
    } catch (e) {
      setDownloadErrorMessage(e.message || 'Unable to download the PDF. Please try again later.');
      setDownloadErrorOpen(true);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleNameClick = row => {
    if (row.source === 'uploaded') {
      handleUploadedNameClick(row);
      return;
    }
    handleDigitalNameClick(row);
  };

  const handleEditAudit = auditId => {
    history.push(`${companyPath}/psm-audit/${auditId}`);
  };

  const handleEditFollowUp = auditId => {
    history.push(`${companyPath}/psm-audit/${auditId}/follow-up`);
  };

  const handleCreateFollowUp = auditId => {
    history.push(`${companyPath}/psm-audit/${auditId}/follow-up`);
  };

  const handleDeleteClick = (row, isFollowUpRow, parentAuditId) => {
    setDeleteConfirm({
      id: row.id,
      isFollowUp: isFollowUpRow,
      isUploaded: row.source === 'uploaded',
      parentAuditId,
      name: getRowLabel(row),
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.isUploaded) {
      onDeleteUploaded(deleteConfirm.id);
    } else {
      onDelete({
        id: deleteConfirm.id,
        isFollowUp: deleteConfirm.isFollowUp,
        parentAuditId: deleteConfirm.parentAuditId,
      });
    }
    setDeleteConfirm(null);
  };

  const renderRow = ({
    row,
    rowKey,
    parentAuditId,
    isFollowUpRow,
    showExpandControl,
    isExpanded,
    onToggleExpand,
    showCreateFollowUp,
    canEditRow,
  }) => {
    const isUploaded = row.source === 'uploaded';
    const isDownloading = isUploaded ? uploadedDownloadList.includes(row.id) : downloadingId === row.id;
    const label = getRowLabel(row);

    return (
      <TableRow key={rowKey} className={classes.tableRow}>
        <TableCell className={classes.nameColumn}>
          <div className={classes.nameCellWrapper}>
            {showExpandControl ? (
              <IconButton
                size="small"
                className={classes.expandButton}
                onClick={onToggleExpand}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            ) : isFollowUpRow ? (
              <span className={classes.expandSpacer} />
            ) : null}
            <MaterialLink
              component="button"
              type="button"
              variant="body2"
              className={`${classes.nameCell} ${isFollowUpRow ? classes.nestedName : ''} ${
                isDownloading ? classes.nameCellDownloading : ''
              }`}
              onClick={() => handleNameClick(row)}
            >
              {isDownloading ? (
                <span className={classes.nameCellDownloadingLabel}>
                  <CircularProgress size={14} />
                  {label}
                </span>
              ) : (
                label
              )}
            </MaterialLink>
          </div>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{row.auditor || '—'}</Typography>
        </TableCell>
        <TableCell align="center">
          <StatusText status={row.status} classes={classes} />
        </TableCell>
        <TableCell align="center" className={classes.auditDateColumn}>
          <Typography variant="body2">{formatDate(row.auditDate)}</Typography>
        </TableCell>
        <TableCell align="right" className={classes.actionsColumn}>
          <div className={classes.actionsCell}>
            {!isUploaded &&
              hasPermissions &&
              (canEditRow ? (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={() => (isFollowUpRow ? handleEditFollowUp(parentAuditId) : handleEditAudit(row.id))}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="View details (read-only)">
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={() => (isFollowUpRow ? handleEditFollowUp(parentAuditId) : handleEditAudit(row.id))}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ))}
            {!isUploaded && isOwner && (
              <Tooltip title="View details (read-only)">
                <IconButton
                  size="small"
                  className={classes.iconButton}
                  onClick={() => (isFollowUpRow ? handleEditFollowUp(parentAuditId) : handleEditAudit(row.id))}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {!isUploaded && showCreateFollowUp && (
              <Tooltip title="Create Follow-Up">
                <IconButton size="small" className={classes.iconButton} onClick={() => handleCreateFollowUp(row.id)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {(isUploaded ? hasPermissions : canEditRow) && (
              <Tooltip title="Delete">
                <span>
                  {!isUploaded && deleteList.includes(row.id) ? (
                    <CircularProgress size={20} />
                  ) : (
                    <IconButton
                      size="small"
                      className={classes.iconButton}
                      onClick={() => handleDeleteClick(row, isFollowUpRow, parentAuditId)}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  )}
                </span>
              </Tooltip>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  if (!topLevelAudits.length) {
    return null;
  }

  return (
    <>
      <TableContainer>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableRow}>
              <TableCell className={classes.nameColumn}>Audit</TableCell>
              <TableCell align="center">Auditor</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center" className={classes.auditDateColumn}>
                Audit Date
              </TableCell>
              <TableCell align="right" className={classes.actionsColumn}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topLevelAudits.map(audit => {
              const isDigital = audit.source === 'digital';
              const hasFollowUp = isDigital && Boolean(audit.followUp && audit.followUp.id != null);
              const isExpanded = Boolean(expandedAuditIds[audit.id]);
              const canEditAuditRow =
                isDigital &&
                (audit.editable ?? canEditPsmAsUser(audit.auditorId, currentUserId, userType)) &&
                audit.status !== 'Completed';
              const canEditFollowUpRow =
                isDigital &&
                (audit.followUp?.editable ?? canEditPsmAsUser(audit.followUp?.auditorId, currentUserId, userType)) &&
                audit.followUp?.status !== 'Completed';
              const showCreateFollowUp = isDigital && hasPermissions && audit.status === 'Completed' && !hasFollowUp;

              return (
                <React.Fragment key={`${audit.source}-${audit.id}`}>
                  {renderRow({
                    row: audit,
                    rowKey: `${audit.source}-${audit.id}`,
                    parentAuditId: audit.id,
                    isFollowUpRow: false,
                    showExpandControl: hasFollowUp,
                    isExpanded,
                    onToggleExpand: () => toggleExpanded(audit.id),
                    showCreateFollowUp,
                    canEditRow: audit.source === 'uploaded' ? false : canEditAuditRow,
                  })}
                  {hasFollowUp &&
                    isExpanded &&
                    renderRow({
                      row: audit.followUp,
                      rowKey: `follow-up-${audit.followUp.id}`,
                      parentAuditId: audit.id,
                      isFollowUpRow: true,
                      showExpandControl: false,
                      showCreateFollowUp: false,
                      canEditRow: canEditFollowUpRow,
                    })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ModalConfirmation
        isModalOpen={Boolean(deleteConfirm)}
        title={
          deleteConfirm?.isUploaded
            ? 'Delete Audit?'
            : deleteConfirm?.isFollowUp
            ? 'Delete Follow-Up?'
            : 'Delete Audit?'
        }
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        confirmDisabled={deleteConfirm && !deleteConfirm.isUploaded && deleteList.includes(deleteConfirm.id)}
      >
        <Typography variant="body2">
          {deleteConfirm?.isFollowUp
            ? 'Are you sure you want to delete this follow-up? This action cannot be undone.'
            : 'Are you sure you want to delete this audit? This action cannot be undone.'}
          {deleteConfirm?.name && (
            <>
              <br />
              <br />
              <strong>{deleteConfirm.name}</strong>
            </>
          )}
        </Typography>
      </ModalConfirmation>

      <Dialog open={notCompletedOpen} onClose={() => setNotCompletedOpen(false)} maxWidth="xs">
        <DialogTitle>PDF Not Available</DialogTitle>
        <DialogContent>
          <DialogContentText>
            PDF will be available after the audit or follow-up is marked as Completed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotCompletedOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={downloadErrorOpen} onClose={() => setDownloadErrorOpen(false)} maxWidth="xs">
        <DialogTitle>Download Failed</DialogTitle>
        <DialogContent>
          <DialogContentText>{downloadErrorMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadErrorOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PsmAuditDigitalList;
